/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Button, ButtonProps } from '@mui/material';
import { styled, Theme, useTheme } from '@mui/material/styles';
import { Partner } from '../LeadFormIndex/types';

interface CustomButtonProps extends ButtonProps {
  primarycolor: string;
  secondarycolor: string | undefined;
  textcolor: string;
  bordercolor: string;
  suppresshover: boolean;
}

function isValidCssColor(color: string): boolean {
  const s = new Option().style;
  s.color = color;
  return s.color !== '';
}

export function getPartnerActionColors(partner: Partner, theme: Theme) {
  const primaryColor = partner.config.styles?.primaryColor;
  const secondaryColor = partner.config.styles?.secondaryColor;
  if (primaryColor === undefined || !isValidCssColor(primaryColor)) {
    return undefined;
  }
  const usesThemeSecondary = secondaryColor === theme.palette.secondary.main;
  return {
    textcolor: usesThemeSecondary ? theme.palette.white.main : primaryColor,
    primarycolor: primaryColor,
    secondarycolor: secondaryColor,
    bordercolor: usesThemeSecondary ? 'transparent' : primaryColor,
  };
}

const idleBackground = (secondarycolor: string | undefined) => secondarycolor || 'white';

const PartnerStyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'suppresshover'
    && prop !== 'primarycolor'
    && prop !== 'secondarycolor'
    && prop !== 'textcolor'
    && prop !== 'bordercolor',
})<CustomButtonProps>(({
  textcolor,
  primarycolor,
  secondarycolor,
  bordercolor,
  suppresshover,
}) => {
  const idle = idleBackground(secondarycolor);
  const reset = {
    color: `${textcolor} !important`,
    background: `${idle} !important`,
    backgroundColor: `${idle} !important`,
  };
  return {
    color: textcolor,
    border: `2px solid ${bordercolor}`,
    background: idle,
    backgroundColor: idle,
    '&:hover:not(.Mui-disabled), &:focus:not(.Mui-disabled), &:focus-visible:not(.Mui-disabled), &:active:not(.Mui-disabled), &.Mui-focusVisible:not(.Mui-disabled)': reset,
    '@media (hover: hover) and (pointer: fine)': {
      ...(!suppresshover && {
        '&:hover:not(.Mui-disabled)': {
          backgroundColor: `${primarycolor} !important`,
          background: `${primarycolor} !important`,
          color: `${idle} !important`,
        },
      }),
    },
  };
});

type PartnerButtonParams = {
  partner: Partner,
  step?: number,
  className?: string,
  testingLabel: 'preloan-welcome-button'|'preloan-submit-button'| 'authorization-submit-button'|'cu-open-dialog-button'|'cu-submit-button'|'complement-offer-button',
  onClick: (event?: React.MouseEvent<HTMLButtonElement>) => void,
  buttonLabel: string,
  disabled?: boolean,
  style?: {},
  endIcon?: React.ReactElement<any, any>,
}

function PartnerButton(params: PartnerButtonParams) {
  const {
    partner,
    className = undefined,
    testingLabel,
    onClick,
    buttonLabel,
    disabled = false,
    style = undefined,
    endIcon = undefined,
  } = params;
  const theme = useTheme();
  const [suppressHover, setSuppressHover] = React.useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.currentTarget.blur();
    setSuppressHover(true);
    onClick(event);
  };
  const handlePointerLeave = () => setSuppressHover(false);
  const componentProps = {
    className,
    'data-pw': testingLabel,
    variant: 'contained' as ButtonProps['variant'],
    color: 'primary' as ButtonProps['color'],
    disableFocusRipple: true,
    onClick: handleClick,
    onPointerLeave: handlePointerLeave,
    onMouseLeave: handlePointerLeave,
    disabled,
    style,
    endIcon,
  };
  const actionColors = getPartnerActionColors(partner, theme);
  if (actionColors) {
    return (
      <PartnerStyledButton
        {...{
          ...componentProps,
          ...actionColors,
          suppresshover: suppressHover,
        }}
      >
        {buttonLabel}
      </PartnerStyledButton>
    );
  }
  return (
    <Button
      {...componentProps}
      sx={{
        '&:focus:not(.Mui-disabled), &:focus-visible:not(.Mui-disabled), &:active:not(.Mui-disabled), &.Mui-focusVisible:not(.Mui-disabled)': {
          backgroundColor: 'primary.main',
        },
        '@media (hover: none), (pointer: coarse)': {
          '&:hover:not(.Mui-disabled)': {
            backgroundColor: 'primary.main',
          },
        },
        ...(suppressHover && {
          '&:hover:not(.Mui-disabled)': {
            backgroundColor: 'primary.main',
          },
        }),
      }}
    >
      {buttonLabel}
    </Button>
  );
}

export default function getPartnerButton(params: PartnerButtonParams) {
  const { step = 0, partner } = params;
  if (step === -1 && partner.partnerName === 'Banca.Me') {
    return undefined;
  }
  return (
    <PartnerButton
      key={`${step}-${params.disabled ? 'disabled' : 'enabled'}`}
      {...params}
    />
  );
}
